import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaUsers,
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaClock,
  FaEdit,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type Batch = {
  id: number;
  name: string;
  topic?: string;
  level?: string;
  courseLevel?: string;
  studentCount?: number;
};

type Quiz = {
  id: number;
  title: string;
  level?: string;
  quizLevel?: string;
  topic?: string;
};

type AssignedBatchInfo = {
  assignmentId: number;
  batchId: number;
  deadline: string;
};

const AssignQuizPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const { student } = useAuth();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [allBatches, setAllBatches] = useState<Batch[]>([]);
  const [assignedBatches, setAssignedBatches] = useState<AssignedBatchInfo[]>(
    []
  );
  const [selectedBatches, setSelectedBatches] = useState<number[]>([]);
  const [deadline, setDeadline] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);

  const [editingAssignmentId, setEditingAssignmentId] = useState<number | null>(null);
  const [editingDeadline, setEditingDeadline] = useState<string>("");
  const [deadlineUpdating, setDeadlineUpdating] = useState(false);
  const [deadlineUpdateError, setDeadlineUpdateError] = useState<string>("");
  const [deadlineUpdateSuccess, setDeadlineUpdateSuccess] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, [quizId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch quiz details
      const quizResponse = await fetch(`${API_BASE}/api/quizzes/${quizId}`, {
        credentials: "include",
      });
      if (quizResponse.ok) {
        const quizData = await quizResponse.json();
        setQuiz(quizData);
        console.log("📋 Quiz level:", quizData.level || quizData.quizLevel);

        // Fetch active batches
        const batchesResponse = await fetch(
          `${API_BASE}/api/quizzes/mentor/active-batches`,
          { credentials: "include" }
        );
        if (batchesResponse.ok) {
          let batchesData: any = await batchesResponse.json();
          if (Array.isArray(batchesData.batches))
            batchesData = batchesData.batches;
          else if (Array.isArray(batchesData.data))
            batchesData = batchesData.data;
          else if (!Array.isArray(batchesData)) batchesData = [];

          console.log("📦 All batches:", batchesData);
          setAllBatches(batchesData);
        }

        // Fetch already assigned batches with deadlines for this quiz
        const assignmentsResponse = await fetch(
          `${API_BASE}/api/quizzes/${quizId}/assigned-batches`,
          { credentials: "include" }
        );
        if (assignmentsResponse.ok) {
          const assignedData = await assignmentsResponse.json();
          console.log("✅ Already assigned batches:", assignedData);
          setAssignedBatches(assignedData);
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Get quiz level
  const quizLevel = (quiz?.level || quiz?.quizLevel || quiz?.topic || "")
    .toString()
    .toUpperCase()
    .trim();

  // Filter batches by quiz level
  const filteredBatches = allBatches.filter((batch) => {
    const batchLevel = (batch.topic || batch.level || batch.courseLevel || "")
      .toString()
      .toUpperCase()
      .trim();
    return quizLevel !== "" && batchLevel === quizLevel;
  });

  // Check if batch is already assigned
  const isBatchAssigned = (batchId: number) => {
    return assignedBatches.some((ab) => ab.batchId === batchId);
  };

  // Get assignment info for a batch
  const getAssignmentInfo = (
    batchId: number
  ): AssignedBatchInfo | undefined => {
    return assignedBatches.find((ab) => ab.batchId === batchId);
  };

  // Format deadline for display
  const formatDeadline = (deadline: string): string => {
    const date = new Date(deadline);
    const now = new Date();
    const isExpired = date < now;

    const formatted = date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return isExpired ? `${formatted} (Expired)` : formatted;
  };

  // Check if deadline is expired
  const isDeadlineExpired = (deadline: string): boolean => {
    return new Date(deadline) < new Date();
  };

  const toDateTimeLocalValue = (deadlineStr: string) =>
    new Date(deadlineStr).toISOString().slice(0, 16);

  const handleEditDeadline = (info: AssignedBatchInfo) => {
    setEditingAssignmentId(info.assignmentId);
    setEditingDeadline(toDateTimeLocalValue(info.deadline));
    setDeadlineUpdateError("");
    setDeadlineUpdateSuccess(null);
  };

  const handleCancelEditDeadline = () => {
    setEditingAssignmentId(null);
    setEditingDeadline("");
    setDeadlineUpdateError("");
  };

  const handleSaveDeadline = async (assignmentId: number) => {
    if (!editingDeadline) {
      setDeadlineUpdateError("Please select a deadline");
      return;
    }
    setDeadlineUpdating(true);
    setDeadlineUpdateError("");
    try {
      const response = await fetch(
        `${API_BASE}/api/quizzes/assignments/${assignmentId}/deadline`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deadline: editingDeadline }),
        }
      );
      if (response.ok) {
        setDeadlineUpdateSuccess(assignmentId);
        setEditingAssignmentId(null);
        const res = await fetch(
          `${API_BASE}/api/quizzes/${quizId}/assigned-batches`,
          { credentials: "include" }
        );
        if (res.ok) setAssignedBatches(await res.json());
      } else {
        const data = await response.json().catch(() => ({}));
        setDeadlineUpdateError(data.error || "Failed to update deadline");
      }
    } catch {
      setDeadlineUpdateError("Network error. Please try again.");
    } finally {
      setDeadlineUpdating(false);
    }
  };

  const handleBatchToggle = (batchId: number) => {
    if (isBatchAssigned(batchId)) {
      // Don't allow toggling already assigned batches
      return;
    }

    setSelectedBatches((prev) =>
      prev.includes(batchId)
        ? prev.filter((id) => id !== batchId)
        : [...prev, batchId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (selectedBatches.length === 0) {
      setError("Please select at least one batch");
      return;
    }
    if (!deadline) {
      setError("Please select a deadline");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE}/api/quizzes/mentor/${quizId}/assign-batches`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            batchIds: selectedBatches,
            deadline: deadline,
          }),
        }
      );

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          if (student?.role === "MENTOR") {
            navigate("/mentor/manage-quizzes");
          } else if (student?.role === "ADMIN") {
            navigate("/admin/manage-quizzes");
          } else {
            navigate("/");
          }
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(
          data.message ||
            data.error ||
            "Failed to assign quiz. It may already be assigned."
        );
      }
    } catch (err) {
      console.error("Error assigning quiz:", err);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaUsers />}
        title="Assign Quiz to Batches"
        breadcrumb={[
          { label: "Home", to: "/" },
          {
            label: "Dashboard",
            to:
              student?.role === "MENTOR"
                ? "/mentor/dashboard"
                : "/admin/dashboard",
          },
          {
            label: "Quizzes",
            to:
              student?.role === "MENTOR"
                ? "/mentor/manage-quizzes"
                : "/admin/manage-quizzes",
          },
          { label: "Assign" },
        ]}
      />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {success && (
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-sm p-4 flex items-center gap-3">
            <FaCheckCircle className="text-green-600 text-xl" />
            <div>
              <h3 className="font-semibold text-green-800">Success!</h3>
              <p className="text-sm text-green-700">
                Quiz assigned successfully. Redirecting...
              </p>
            </div>
          </div>
        )}

        {/* Quiz Info Card */}
        {quiz && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Quiz Details
            </h3>
            <div className="space-y-2">
              <div>
                <span className="font-semibold text-gray-700">Title: </span>
                <span className="text-gray-900">{quiz.title}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Level: </span>
                <span className="inline-flex items-center px-3 py-1 rounded-sm text-xs font-semibold bg-blue-100 text-blue-800">
                  {quizLevel}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Form */}
        <div className="bg-white rounded-sm shadow-md p-6 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Select Batches */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Batches * (Level: {quizLevel})
              </label>
              {filteredBatches.length === 0 ? (
                <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-sm">
                  <FaExclamationTriangle className="mx-auto text-amber-500 text-3xl mb-2" />
                  <p className="text-amber-700 font-semibold">
                    No {quizLevel} level batches available
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    Create a batch with topic "{quizLevel}" to assign this quiz.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-sm p-3">
                  {filteredBatches.map((batch) => {
                    const isAssigned = isBatchAssigned(batch.id);
                    const assignmentInfo = getAssignmentInfo(batch.id);
                    const deadlineExpired =
                      assignmentInfo &&
                      isDeadlineExpired(assignmentInfo.deadline);

                    return (
                      <label
                        key={batch.id}
                        className={`flex items-center gap-3 p-3 rounded-sm transition-colors ${
                          isAssigned
                            ? deadlineExpired
                              ? "bg-gray-50 border border-gray-200 cursor-not-allowed"
                              : "bg-green-50 border border-green-200 cursor-not-allowed"
                            : "bg-gray-50 hover:bg-blue-50 cursor-pointer"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={
                            isAssigned || selectedBatches.includes(batch.id)
                          }
                          onChange={() => handleBatchToggle(batch.id)}
                          disabled={isAssigned}
                          className="w-5 h-5 text-[#009fe3] focus:ring-[#009fe3] rounded disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`font-medium ${
                                isAssigned
                                  ? deadlineExpired
                                    ? "text-gray-700"
                                    : "text-green-800"
                                  : "text-gray-900"
                              }`}
                            >
                              {batch.name}
                            </span>
                            {isAssigned && (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm ${
                                  deadlineExpired
                                    ? "bg-gray-100 text-gray-600"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                <FaCheckCircle size={10} />
                                {deadlineExpired
                                  ? "Deadline Passed"
                                  : "Already Assigned"}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            Topic:{" "}
                            {batch.topic || batch.level || batch.courseLevel}
                            {batch.studentCount &&
                              ` • ${batch.studentCount} students`}
                          </div>
                          {isAssigned && assignmentInfo && (
                            <div className="mt-1">
                              {editingAssignmentId === assignmentInfo.assignmentId ? (
                                <div className="flex items-center gap-2 flex-wrap mt-1">
                                  <input
                                    type="datetime-local"
                                    value={editingDeadline}
                                    onChange={(e) => setEditingDeadline(e.target.value)}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); handleSaveDeadline(assignmentInfo.assignmentId); }}
                                    disabled={deadlineUpdating}
                                    className="flex items-center gap-1 px-2 py-1 bg-[#009fe3] text-white rounded text-xs hover:bg-cyan-600 disabled:opacity-50"
                                  >
                                    {deadlineUpdating ? <FaSpinner className="animate-spin" size={10} /> : <FaSave size={10} />}
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); handleCancelEditDeadline(); }}
                                    className="flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                                  >
                                    <FaTimes size={10} />
                                    Cancel
                                  </button>
                                  {deadlineUpdateError && (
                                    <span className="text-xs text-red-600">{deadlineUpdateError}</span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className={`flex items-center gap-1 text-xs ${deadlineExpired ? "text-gray-600" : "text-green-600"}`}>
                                    <FaClock size={10} />
                                    <span>Deadline: {formatDeadline(assignmentInfo.deadline)}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); handleEditDeadline(assignmentInfo); }}
                                    className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs hover:bg-amber-200 transition-colors"
                                  >
                                    <FaEdit size={10} />
                                    Edit
                                  </button>
                                  {deadlineUpdateSuccess === assignmentInfo.assignmentId && (
                                    <span className="text-xs text-green-600 font-medium">Updated!</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                Selected: {selectedBatches.length} batch(es) • Already assigned:{" "}
                {assignedBatches.length} batch(es)
              </div>
            </div>

            {/* Deadline */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Submission Deadline *
              </label>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={
                  submitting ||
                  success ||
                  filteredBatches.length === 0 ||
                  selectedBatches.length === 0
                }
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-sm font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <FaUsers />
                    Assign to Batches
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={submitting}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-sm font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AssignQuizPage;
